import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import './HomeScreen.css';

const HomeScreen: React.FC = () => {
  const { currentSport, theme } = useTheme();
  const { user, loading } = useAuth();

  const featureCards = [
    {
      id: 'online-coach',
      title: 'Your Online Coach',
      description: 'Professional coaching without the cost—shot analysis, video feedback, injury prevention & recovery',
      icon: '👨‍🏫',
      path: '/game-analysis',
      color: theme.colors.primary
    },
    {
      id: 'video-analysis',
      title: 'Video Analysis',
      description: 'Upload your videos and get AI-powered feedback on your technique',
      icon: '📹',
      path: '/video-analysis',
      color: theme.colors.primary
    },
    {
      id: 'skill-assessment',
      title: 'Skill Assessment',
      description: 'Take our assessment to identify your strengths and areas for improvement',
      icon: '📊',
      path: '/skill-assessment',
      color: theme.colors.secondary
    },
    {
      id: 'drills',
      title: 'Drill Library',
      description: 'Access hundreds of drills organized by skill level and focus area',
      icon: '⚡',
      path: '/drills',
      color: theme.colors.accent
    },
    {
      id: 'strategies',
      title: 'Strategy Guide',
      description: 'Learn advanced strategies and tactics to elevate your game',
      icon: '🧠',
      path: '/strategies',
      color: theme.colors.primary
    },
    {
      id: 'equipment',
      title: 'Equipment Guide',
      description: 'Get recommendations on the best equipment for your skill level',
      icon: '🛠️',
      path: '/equipment',
      color: theme.colors.secondary
    },
    {
      id: 'fundamentals',
      title: 'Fundamentals',
      description: 'Master the basics with our comprehensive fundamentals training',
      icon: '📚',
      path: '/fundamentals',
      color: theme.colors.accent
    }
  ];

  const quickStartItems = [
    {
      title: 'Your Online Coach',
      description: 'Professional coaching without the cost—shot analysis, video feedback & recovery',
      action: 'Get Started',
      path: '/game-analysis',
      icon: '👨‍🏫'
    },
    {
      title: 'Take Skill Assessment',
      description: 'Start here to get personalized recommendations',
      action: 'Begin Assessment',
      path: '/skill-assessment',
      icon: '🎯'
    },
    {
      title: 'Upload Your First Video',
      description: 'Get instant feedback on your technique',
      action: 'Upload Video',
      path: '/video-analysis',
      icon: '📱'
    },
    {
      title: 'Practice Fundamentals',
      description: 'Build a solid foundation with basic drills',
      action: 'Start Training',
      path: '/fundamentals',
      icon: '🏋️'
    }
  ];

  return (
    <div className="home-screen">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                Welcome to {currentSport.displayName}
              </h1>
              <p className="hero-description">
                {currentSport.description}
              </p>
              <div className="hero-actions">
                <Link to="/skill-assessment" className="hero-button primary">
                  Start Your Journey
                </Link>
                <Link to="/video-analysis" className="hero-button secondary">
                  Upload Video
                </Link>
              </div>
            </div>
            <div className="hero-visual">
              {currentSport.id === 'pickleball' ? (
                <img 
                  src="/pickleball-icon.svg" 
                  alt="Pickleball Logo" 
                  className="hero-logo"
                />
              ) : currentSport.id === 'golf' ? (
                <span className="hero-icon">⛳</span>
              ) : currentSport.id === 'tennis' ? (
                <img 
                  src="/tennis-icon.svg" 
                  alt="Tennis Logo" 
                  className="hero-logo"
                />
              ) : currentSport.id === 'hiking' ? (
                <span className="hero-icon">🥾</span>
              ) : currentSport.id === 'padel' ? (
                <img 
                  src="/padel-icon.svg" 
                  alt="Padel Logo" 
                  className="hero-logo"
                />
              ) : currentSport.id === 'badminton' ? (
                <img 
                  src="/badminton-icon.svg" 
                  alt="Badminton Logo" 
                  className="hero-logo"
                />
              ) : currentSport.id === 'racquetball' ? (
                <img 
                  src="/racquetball-icon.svg" 
                  alt="Racquetball Logo" 
                  className="hero-logo"
                />
              ) : currentSport.id === 'squash' ? (
                <img 
                  src="/squash-icon.svg" 
                  alt="Squash Logo" 
                  className="hero-logo"
                />
              ) : currentSport.id === 'running' ? (
                <span className="hero-icon">🏃</span>
              ) : currentSport.id === 'cycling' ? (
                <span className="hero-icon">🚴</span>
              ) : currentSport.id === 'swimming' ? (
                <span className="hero-icon">🏊</span>
              ) : currentSport.id === 'strength' ? (
                <span className="hero-icon">💪</span>
              ) : (
                <span className="hero-icon">🏓</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Start Section */}
      <section className="quick-start-section">
        <div className="container">
          <h2 className="section-title">Quick Start</h2>
          <div className="quick-start-grid">
            {quickStartItems.map((item) => (
              <div key={item.title} className="quick-start-card">
                <div className="quick-start-icon">{item.icon}</div>
                <h3 className="quick-start-title">{item.title}</h3>
                <p className="quick-start-description">{item.description}</p>
                <Link to={item.path} className="quick-start-button">
                  {item.action}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Everything You Need to Excel</h2>
          <div className="features-grid">
            {featureCards.map((feature) => (
              <Link key={feature.id} to={feature.path} className="feature-card">
                <div className="feature-icon" style={{ color: feature.color }}>
                  {feature.icon}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <div className="feature-arrow">→</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Only show if user is not logged in */}
      {!loading && !user && (
        <section className="cta-section" style={{ backgroundColor: theme.colors.surface }}>
          <div className="container">
            <div className="cta-content">
              <h2 className="cta-title">Ready to Transform Your Game?</h2>
              <p className="cta-description">
                Join thousands of players who have improved their skills with AI-powered coaching
              </p>
              <div className="cta-actions">
                <Link to="/signup" className="cta-button primary">
                  Get Started Free
                </Link>
                <Link to="/signin" className="cta-button secondary">
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Welcome Section - Show if user is logged in */}
      {!loading && user && (
        <section className="welcome-section" style={{ backgroundColor: theme.colors.surface }}>
          <div className="container">
            <div className="welcome-content">
              <h2 className="welcome-title">Welcome back, {user.name}!</h2>
              <p className="welcome-description">
                Continue your journey to improve your {currentSport.name} skills
              </p>
              <div className="welcome-actions">
                <Link to="/skill-assessment" className="welcome-button primary" style={{ backgroundColor: 'var(--shared-primary)' }}>
                  Continue Learning
                </Link>
                <Link to="/blog" className="welcome-button secondary" style={{ borderColor: 'var(--shared-primary)', color: 'var(--shared-primary)' }}>
                  📝 Read Blog
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default HomeScreen;
